import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { FiHome as Home, FiDatabase as Database, FiGitBranch as GitBranch, FiPlay as Play, FiActivity as Radio, FiTarget as Target, FiHelpCircle as HelpCircle, FiInfo as Info } from 'react-icons/fi';
import DataOpsGettingStartedBanner from './DataOpsGettingStartedBanner';
import { useDataOpsOnboardingStatus } from '../../../hooks/useDataOpsOnboardingStatus';
import { demoModeManager } from '../../../services/demoModeManager';
import HelpPanel from '../../../components/common/HelpPanel';
import { DataOpsHelp } from '../help';

type DataOpsTab = 'overview' | 'catalog' | 'lineage' | 'runs' | 'events' | 'impact';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const DataOpsLayout = () => {
  const location = useLocation();
  const { isComplete: onboardingComplete, refresh: refreshOnboarding } = useDataOpsOnboardingStatus();
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return localStorage.getItem('dataops_onboarding_dismissed') !== 'true';
  });
  const [showHelpPanel, setShowHelpPanel] = useState(false);

  // Refresh onboarding status on route change to pick up newly completed steps
  useEffect(() => {
    refreshOnboarding();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Auto-dismiss when all onboarding steps are complete
  useEffect(() => {
    if (onboardingComplete) {
      localStorage.setItem('dataops_onboarding_dismissed', 'true');
      setShowOnboarding(false);
    }
  }, [onboardingComplete]);

  const handleDismissOnboarding = () => {
    localStorage.setItem('dataops_onboarding_dismissed', 'true');
    setShowOnboarding(false);
  };

  const handleShowOnboarding = () => {
    localStorage.removeItem('dataops_onboarding_dismissed');
    setShowOnboarding(true);
  };

  const dataOpsNavItems: NavItem[] = [
    { path: '/dataops/overview', label: 'Overview', icon: Home },
    { path: '/dataops/catalog', label: 'Catalog', icon: Database },
    { path: '/dataops/lineage', label: 'Lineage', icon: GitBranch },
    { path: '/dataops/runs', label: 'Runs', icon: Play },
    { path: '/dataops/events', label: 'Events', icon: Radio },
    { path: '/dataops/impact', label: 'Impact', icon: Target }
  ];

  const isActive = (path: string): boolean => {
    return location.pathname === path || (path === '/dataops/overview' && location.pathname === '/dataops');
  };

  // Determine active tab from current path for context-aware help
  const activeTab = useMemo((): DataOpsTab => {
    const path = location.pathname;
    if (path.includes('/dataops/catalog')) return 'catalog';
    if (path.includes('/dataops/lineage')) return 'lineage';
    if (path.includes('/dataops/runs')) return 'runs';
    if (path.includes('/dataops/events')) return 'events';
    if (path.includes('/dataops/impact')) return 'impact';
    return 'overview'; // Default to overview
  }, [location.pathname]);

  // Get dynamic help panel title based on active tab
  const helpPanelTitle = useMemo((): string => {
    const titles: Record<DataOpsTab, string> = {
      overview: 'Overview Help',
      catalog: 'Catalog Help',
      lineage: 'Lineage Help',
      runs: 'Runs Help',
      events: 'Events Help',
      impact: 'Impact Help'
    };
    return titles[activeTab] || 'Help';
  }, [activeTab]);

  return (
    <div>
      {/* DataOps Sub-Navigation */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <nav className="flex space-x-8" aria-label="DataOps navigation">
              {dataOpsNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`${
                      isActive(item.path)
                        ? 'border-blue-500 text-gray-900 dark:text-white'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center transition-colors`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
            {/* Action buttons */}
            <div className="flex items-center gap-3">
              {/* Getting Started banner toggle - hidden when all steps complete */}
              {!showOnboarding && !onboardingComplete && (
                <button
                  onClick={handleShowOnboarding}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                  title="Getting Started"
                  aria-label="Getting Started"
                >
                  <Info className="w-5 h-5" />
                </button>
              )}
              {/* Help panel toggle */}
              <button
                onClick={() => setShowHelpPanel(true)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                title="Help"
                aria-label="Help"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Onboarding Banner - hidden in demo mode */}
      {showOnboarding && !demoModeManager.getDemoMode().isDemoMode && (
        <div className="mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <DataOpsGettingStartedBanner onDismiss={handleDismissOnboarding} />
        </div>
      )}

      {/* DataOps Content */}
      <Outlet />

      {/* Help Panel */}
      <HelpPanel
        isOpen={showHelpPanel}
        onClose={() => setShowHelpPanel(false)}
        title={helpPanelTitle}
      >
        <DataOpsHelp activeTab={activeTab} />
      </HelpPanel>
    </div>
  );
};

export default DataOpsLayout;
